/* rd-bookings — Bookings tab. Header idiom shared with the shipped /book catalog
   (rendered into the ClientShell header slot); Past rows + empty states from the
   confirmed mockup (docs/mockups/client-dashboard-redesign/bookings-tab.html).
   Static radii/fonts are literals (no `.page` --r-* scope here); all theme values
   resolve through the injected foundation tokens — no hardcoded theme hex. */

/* ── Header (into the ClientShell header slot) — mirrors the catalog ── */
.pageHeaderSection {
  position: relative;
  padding: 0 18px 12px;
}
.back {
  position: absolute;
  top: 2px;
  left: 18px;
}
.pageTitleBlock {
  text-align: center;
  padding: 0 64px; /* clears the 44px back button */
}
.pageTitle {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 25px;
  letter-spacing: -0.006em;
  line-height: 1.1;
  margin-bottom: 3px;
  color: var(--text-primary);
}
.pageSubtitle {
  font-size: 12.5px;
  color: var(--text-secondary);
  line-height: 1.35;
}

/* ── Segmented Upcoming / Past ── */
.tabSegmented {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px;
  padding: 3px;
  margin: 0 18px;
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  border-radius: 14px;
  box-shadow: var(--glass-shadow);
  position: relative;
  overflow: hidden;
}
.tabSegmented::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.16) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.tabSegment {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  position: relative;
  z-index: 1;
  padding: 9px 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-tertiary);
  text-align: center;
}
.tabSegment.active {
  color: var(--text-primary);
  background: rgba(var(--accent-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--accent-rgb), 0.45), 0 0 18px rgba(var(--accent-rgb), 0.18);
}

/* ── Body section ── */
.section {
  padding: 8px 20px 0;
}

/* ── Past rows — thin, photo-less (mockup Frame 2) ── */
.pastList {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pastRow {
  position: relative;
  width: 100%;
  appearance: none;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 14px;
  background: var(--glass-bg);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  overflow: hidden;
  text-align: left;
}
.pastRow::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.1) 0%, rgba(var(--accent-light-rgb), 0.02) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.pastRow > * {
  position: relative;
  z-index: 1;
}
.pastDate {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 10px;
  background: var(--bg-inset);
  border: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.pastMonth {
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  line-height: 1.2;
}
.pastDay {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 500;
  font-size: 19px;
  color: var(--text-secondary);
  line-height: 1.05;
}
.pastInfo {
  flex: 1;
  min-width: 0;
}
.pastTitle {
  display: block;
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 500;
  font-size: 15px;
  letter-spacing: -0.018em;
  color: var(--text-primary);
  line-height: 1.2;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pastMeta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-tertiary);
  white-space: nowrap;
  overflow: hidden;
}
.pastMeta svg {
  width: 11px;
  height: 11px;
  stroke-width: 1.8;
  flex-shrink: 0;
}
.pastDot {
  opacity: 0.5;
}
.pastChev {
  width: 13px;
  height: 13px;
  color: var(--text-tertiary);
  stroke-width: 2.2;
  flex-shrink: 0;
}
/* Cancelled — the whole row dims; the pill sits before the chevron */
.pastRowCancelled .pastTitle {
  color: var(--text-tertiary);
}
.pastRowCancelled .pastDay {
  color: var(--text-muted);
}

/* Terminal status pill (thin past row); Completed uses the normal accent treatment. */
.statusPill {
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.005em;
  white-space: nowrap;
  flex-shrink: 0;
  background: var(--status-pill-bg);
  color: var(--status-pill-text);
}
.statusPillCancelled {
  background: var(--border-subtle);
  color: var(--text-muted);
}

/* Show earlier sessions — ghost pill */
.showMore {
  appearance: none;
  -webkit-appearance: none;
  border: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 12px auto 0;
  padding: 9px 18px;
  border-radius: 9999px;
  background: transparent;
  box-shadow: inset 0 0 0 1px var(--border-soft);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-secondary);
}
.showMore:disabled {
  color: var(--text-tertiary);
}
.showMore svg {
  width: 13px;
  height: 13px;
  stroke-width: 2;
  color: var(--text-tertiary);
}

/* Loading / error states */
.stateBlock {
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}
